import { ConfigOption, FormlyFieldConfig } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { FormlyCheckboxComponent } from './components/formly/formly-checkbox/formly-checkbox.component';
import { FormlySwitchComponent } from './components/formly/formly-switch/formly-switch.component';
import { FormlyPasswordComponent } from './components/formly/formly-password/formly-password.component';
import { FormlyDatePickerComponent } from './components/formly/formly-date-picker/formly-date-picker.component';
import { UploadInputComponent } from './components/formly/upload-input/upload-input.component';
import { FormlyTextareaComponent } from './components/formly/formly-textarea/formly-textarea.component';
import { AbstractControl, Validators } from '@angular/forms';
import { FormlyMultiCheckboxComponent } from './components/formly/formly-multi-checkbox/formly-multi-checkbox.component';
import { map } from 'rxjs';

class TranslateExtensionFactory {
  constructor(private translate: TranslateService) {}
  prePopulate(field: FormlyFieldConfig) {
    const to = field.templateOptions || {};
    if (!to['translate']) return;

    to['textLabel'] = to.label || 'VALIDATION.ITEM';
    const options = Array.isArray(to.options) ? to.options : [];

    field.expressionProperties = {
      ...(field.expressionProperties || {}),
      ...(Array.isArray(to.options)
        ? {
            'templateOptions.options': this.translate
              .stream(options.map((o) => o.label))
              .pipe(
                map((translations) =>
                  options.map((o) => ({
                    ...o,
                    label: translations[o.label],
                  }))
                )
              ),
          }
        : {}),
      ...(to.label
        ? {
            'templateOptions.label': this.translate.stream(to.label),
          }
        : {}),
      ...(to.placeholder
        ? {
            'templateOptions.placeholder': this.translate.stream(
              to.placeholder
            ),
          }
        : {}),

      ...(to.description
        ? {
            'templateOptions.description': this.translate.stream(
              to.description
            ),
          }
        : {}),
      ...(to['title']
        ? { 'templateOptions.title': this.translate.stream(to['title']) }
        : {}),
      ...(to['textBefore']
        ? {
            'templateOptions.textBefore': this.translate.stream(
              to['textBefore']
            ),
          }
        : {}),
      ...(to['textAfter']
        ? {
            'templateOptions.textAfter': this.translate.stream(to['textAfter']),
          }
        : {}),
    };

    if (field.validation && field.validation.messages) {
      const messages = field.validation.messages;

      Object.keys(messages).forEach((key) => {
        const original = messages[key];
        if (typeof original === 'string') {
          messages[key] = () =>
            this.translate.stream(original).pipe(map((value) => value));
        } else if (typeof original === 'function') {
          messages[key] = (err, f) => {
            const msg = original(err, f);
            return typeof msg === 'string'
              ? this.translate.stream(msg).pipe(map((value) => value))
              : msg;
          };
        }
      });
    }

    // Force validation refresh on language change
    this.translate.onLangChange.subscribe(() => {
      field.formControl?.updateValueAndValidity();
    });
  }
}

const passwordMatchValidator = (control: AbstractControl) => {
  const { password, confirm_password } = control.value;
  if (!password || !confirm_password) {
    return null;
  }
  if (confirm_password === password) {
    return null;
  }

  return { password_match: true };
};

export const formlyConfig = (translate: TranslateService): ConfigOption => ({
  extensions: [
    {
      name: 'translate',
      extension: new TranslateExtensionFactory(translate),
    },
  ],
  validationMessages: [
    {
      name: 'required',
      message: (_, field) => {
        if (field && ['select', 'date-picker'].includes(field.type as string)) {
          return translate.instant('VALIDATION.REQUIRED_SELECT', {
            field: translate.instant(
              field.templateOptions
                ? field.templateOptions['textLabel']
                : 'VALIDATION.ITEM'
            ),
          });
        }

        return translate.instant('VALIDATION.REQUIRED', {
          field: translate.instant(
            field.templateOptions
              ? field.templateOptions['textLabel']
              : 'VALIDATION.ITEM'
          ),
        });
      },
    },
    {
      name: 'minLength',
      message: (_, field) => {
        return translate.instant('VALIDATION.MIN', {
          field: translate.instant(
            field.templateOptions
              ? field.templateOptions['textLabel']
              : 'VALIDATION.ITEM'
          ),
          min: field.templateOptions?.minLength,
        });
      },
    },
    {
      name: 'maxLength',
      message: (_, field) => {
        return translate.instant('VALIDATION.MAX', {
          field: translate.instant(
            field.templateOptions
              ? field.templateOptions['textLabel']
              : 'VALIDATION.ITEM'
          ),
          max: field.templateOptions?.maxLength,
        });
      },
    },
  ],
  types: [
    {
      name: 'custom-checkbox',
      component: FormlyCheckboxComponent,
    },
    {
      name: 'formly-switch',
      component: FormlySwitchComponent,
    },
    {
      name: 'password',
      component: FormlyPasswordComponent,
    },
    {
      name: 'date-picker',
      component: FormlyDatePickerComponent,
    },
    {
      name: 'formly-upload',
      component: UploadInputComponent,
    },
    {
      name: 'formly-textarea',
      component: FormlyTextareaComponent,
    },
    {
      name: 'formly-multi-checkbox',
      component: FormlyMultiCheckboxComponent,
    },
  ],
  validators: [
    {
      name: 'password-match',
      validation: passwordMatchValidator,
    },
    { name: 'email', validation: Validators.email },
  ],
});
